import { SubmitStore } from "Stores/RESTStore/SubmitStore";
import { projectsDataStore } from "Stores/ProjectsStore";
import { API } from "General/API";
import { action, runInAction, makeObservable } from "mobx";
import { API_URL } from "General/APIEndpoints";
import { handleStoreState } from "Stores/RESTStore/CoreStore";
import { tasksDataStore } from "./TasksDataStore";
import { taskAttachmentsStore } from "./TaskAttachmentsStore";
import { taskModalStore } from "./TaskModalStore";
import { userInfoStore } from "Stores/UserInfoStore";
import { dayjs } from "Config/dayjs";

export interface TaskSubmitStoreType extends API_TaskRequestData {
  actions: API_TaskActions;
}

export interface TaskDenyFormData {
  comment: string;
}

class TaskSubmitStore extends SubmitStore<TaskSubmitStoreType> {
  constructor() {
    super();

    makeObservable(this, {
      clearAll: action.bound,
      updateFulfillment: action.bound,
      updateState: action.bound,
      save: action.bound,
      createTask: action.bound,
      editTask: action.bound,
      deleteTask: action.bound,
    });
  }

  url(taskId?: number) {
    if (taskId) {
      return API_URL.TASKS.getTask(projectsDataStore.projectId, taskId);
    } else {
      return API_URL.TASKS.createTask(projectsDataStore.projectId);
    }
  }

  clearAll() {
    this.clear();
    taskAttachmentsStore.clearAll();
  }

  @handleStoreState()
  async createTask(formData: API_TaskRequestData) {
    const attachments = taskAttachmentsStore.getFileIds();
    formData.attachments = attachments;
    formData.fulfillment = 0;
    formData.global = false;
    formData.ownerId = userInfoStore.id;
    formData.lastChangeDate = dayjs().format();
    formData.state = "OPEN";
    try {
      const mergedTaskData = {
        ...this.data,
        ...formData,
      };
      const response = await API.newData(this.url(), mergedTaskData);
      taskAttachmentsStore.clearNewAttachedFiles(true);
      runInAction(() => {
        this.update(response);
      });
      if (taskModalStore.isVisible) {
        taskModalStore.toggleModal();
      }
      tasksDataStore.fetch();
      return response;
    } catch (err) {
      await taskAttachmentsStore.checkRights(err, "TASK_CREATE");
      throw err;
    }
  }

  @handleStoreState()
  async editTask(formData: API_TaskRequestData) {
    const attachments = taskAttachmentsStore.getFileIds();
    formData.attachments = attachments;
    try {
      const mergedTaskData: Omit<API_TaskData, "attachments"> & API_TaskRequestData = {
        ...this.data,
        ...formData,
      };
      const response: API_TaskData = await API.editData(this.url(mergedTaskData.id), mergedTaskData);
      taskAttachmentsStore.clearNewAttachedFiles();
      tasksDataStore.fetch();
      runInAction(async () => {
        if (response) {
          taskAttachmentsStore.addInitialFiles(response.attachments);
        }
      });
    } catch (err) {
      await taskAttachmentsStore.checkRights(err, "TASK_EDIT");
      throw err;
    }
  }

  @handleStoreState()
  async deleteTask() {
    try {
      await API.deleteData(this.url(this.data.id));
    } catch (err) {
      throw err;
    }
  }

  @handleStoreState()
  async acceptTask() {
    try {
      await API.editData(API_URL.TASKS.acceptTask(projectsDataStore.projectId, this.data.id), {});
    } catch (err) {
      throw err;
    }
  }

  @handleStoreState()
  async denyTask(data: TaskDenyFormData) {
    try {
      await API.editData(API_URL.TASKS.declineTask(projectsDataStore.projectId, this.data.id), data);
    } catch (err) {
      throw err;
    }
  }

  updateFulfillment(value: number) {
    const updatedData = { fulfillment: value };
    if (value === 0) {
      Object.assign(updatedData, { state: "OPEN" });
    }
    if (value === 100) {
      Object.assign(updatedData, { state: "FINISHED" });
    }
    if (value < 100 && value !== this.data.fulfillment) {
      Object.assign(updatedData, { state: "IN_PROGRESS" });
    }
    this.update(updatedData as TaskSubmitStoreType);
    return updatedData;
  }

  updateState(state: API_SUBTASKDATA_STATE) {
    const updatedData = { state };
    if (state === "FINISHED") {
      Object.assign(updatedData, { fulfillment: 100 });
    }
    this.update(updatedData as TaskSubmitStoreType);
    return updatedData;
  }

  async save() {
    const attachments = taskAttachmentsStore.getFileIds();
    const response = await API.editData(this.url(this.data.id), {
      ...this.data,
      attachments,
    });
    runInAction(() => {
      if (response) {
        tasksDataStore.fetch();
      }
    });
  }
}

const taskSubmitStore = new TaskSubmitStore();
export { taskSubmitStore, TaskSubmitStore };
